import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useAuth, useMessage } from "../global-components/src";
import { Stamp } from "../global-components/src/types";
import { t } from "../global-components/src/i18nTranslate";
import { logEvent } from "../components/analytics-tracker";
import { errorMessage } from "../global-components/src/utils/text";
import { api } from "../services/api";
import ItemList from "../components/ItemList/ItemList";
import { LeftContent, RightContent } from "../global-components/src/components/screen/left-content";
import Screen from "./Layouts/Screen/Screen";
import Spinner from "../global-components/src/components/spinner/spiner";
import { BadgeCheck } from "lucide-react";

const MyStamps = () => {
    const [loading, setLoading] = useState(false);
    const { setMessage } = useMessage();
    const { token, user } = useAuth();
    const hasStamp = (stamp: Stamp) => stamps?.includes(stamp);
    const [stamps, setStamps] = useState<Stamp[]>([]);
    const [confirm, setConfirm] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if(user?.stamps){
            setStamps(user.stamps);
        }
    }, [user?.stamps]);

    const toggleStamp = (stamp: Stamp) => {
        setStamps((prev) => {
            // Se já existe, apenas remove
            if (prev.includes(stamp)) {
                return prev.filter((s) => s !== stamp);
            }

            // Se estiver adicionando "black", remove "blue"
            if (stamp === 'black') {
                return [...prev.filter((s) => s !== 'blue'), 'black'];
            }

            // (Opcional) Se adicionar "blue", remove "black"
            if (stamp === 'blue') {
                return [...prev.filter((s) => s !== 'black'), 'blue'];
            }

            // Caso normal
            return [...prev, stamp];
        });
    };

    const submit = async () =>{
        setLoading(true);
        setMessage("", "error");

        logEvent("novo_verificado", {
            categoria: "Verificado",
            acao: "Novo verificado",
        });

        try {
            await api(token).post("/user/stamps", {
                stamps, confirm
            });

            setMessage(t("meaningSeals.success"), "success");
            navigate(`/@${user?.user_name}`);
        } catch (err: any) {
            const messageError = errorMessage(err);
            setMessage(messageError, "error")
            console.error(messageError);
            console.error(err);
        }
        setLoading(false)
    }
      
    return (
        <>
            <Screen paddingTopTitle={true} title={window.innerWidth <= 850 ? t("settings.verified") : " "}>
                <Helmet>
                        <title>Paçoca - Verificado</title>
                        <meta name="description" content={`Seja verificado no Paçoca, a rede dos brasileiros!`}/>
                        <meta name="keywords" content={`Paçoca, rede social brasileira, Verificado, premium, pagamento}`}/>
                        <meta name="author" content="Paçoca Inc."/>
                        <meta property="og:title" content={`Paçoca - Verificado`}/>
                        <meta property="og:description" content={`Seja verificado no Paçoca, a rede dos brasileiros!`}/>
                        <meta property="og:image" content="/img/link-preview.png?v=1" />
                        <meta property="og:image:width" content="1200" />
                        <meta property="og:image:height" content="630" />
                        <meta property="og:url" content={window.location.href}/>
                        <meta property="og:type" content="website"/>
                        <meta name="twitter:card" content="summary_large_image"/>
                        <meta name="twitter:title" content={`Paçoca - Verificado`}/>
                        <meta name="twitter:description" content={`Seja verificado no Paçoca, a rede dos brasileiros!`}/>
                        <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>

                <LeftContent/>
                <RightContent/>

                <div className="padding-big-screen">
                    <div className="bank-tx">
                        <div className="bank-hero">
                            <div className="foc-premium-icon">
                                <BadgeCheck size={26} />
                            </div>
                            <h5 className="bank-hero-title">
                                {t("meaningSeals.request")}
                            </h5>
                        </div>
                        {/* <h1 className="bank-hero-title text-center">{t("meaningSeals.request")}</h1> */}

                        <form className="padding-baig-screen">
                            <div className="a-center">
                                <p>{t("meaningSeals.text1")}</p>
                                <p>{t("meaningSeals.text2")}</p>

                                <ItemList>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id="stamp-autism" checked={hasStamp("autism")} onChange={() => toggleStamp("autism")}/>
                                        <label className="cursor-pointer form-check-label d-flex align-items-center" htmlFor="stamp-autism" style={{ gap: "10px", margin: 0 }}>
                                        <img height="17" src="/img/selos/autism.png" loading="lazy" />
                                            {t("meaningSeals.autism.title")}
                                        </label>
                                    </div>
                                </ItemList>
                                <ItemList>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id="stamp-lgbt" checked={hasStamp("lgbt")} onChange={() => toggleStamp("lgbt")}/>
                                        <label className="cursor-pointer form-check-label d-flex align-items-center" htmlFor="stamp-lgbt" style={{ gap: "10px", margin: 0 }}>
                                        <img height="17" src="/img/selos/lgbt.png" loading="lazy" />
                                            {t("meaningSeals.lgbt.title")}
                                        </label>
                                    </div>
                                </ItemList>
                                <ItemList>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id="stamp-hidden-deficiencies" checked={hasStamp("hidden_deficiencies")} onChange={() => toggleStamp("hidden_deficiencies")}/>
                                        <label className="cursor-pointer form-check-label d-flex align-items-center" htmlFor="stamp-hidden-deficiencies" style={{ gap: "10px", margin: 0 }}>
                                        <img height="17" src="/img/selos/hidden_deficiencies.png" loading="lazy" />
                                            {t("meaningSeals.hidden_deficiencies.title")}
                                        </label>
                                    </div>
                                </ItemList>

                                <div className="form-check d-flex mt-3 mb-2">
                                    <input 
                                        className="form-check-input mt-1" 
                                        type="checkbox" 
                                        id="confirm" 
                                        checked={confirm} 
                                        onChange={() => setConfirm(!confirm)} 
                                    />
                                    <label style={{ marginTop: 0, marginRight: 0 }} className="cursor-pointer form-check-label" htmlFor="confirm">
                                        {t("meaningSeals.confirm")}
                                    </label>
                                </div>

                                <p>{t("meaningSeals.obs")}</p>

                                <div className="space-button"></div>
                                <button disabled={!confirm || loading || (!hasStamp("hidden_deficiencies") && !hasStamp("autism") && !hasStamp("lgbt"))} onClick={submit}  className="foc-btn foc-btn-primary">
                                    {loading ? <Spinner size="20px" /> : (
                                        t("meaningSeals.request")
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </Screen>
        </>
    );
};

export default MyStamps;